import React, { useState, useEffect } from 'react';
import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import FieldInput from "../../../../components/ModuleCommon/FormBuilder/FieldInput";
import FieldDropdown from "../../../../components/ModuleCommon/FormBuilder/FieldDropdown";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { invoiceApi } from '../InvoiceApi';
import httpClient from '../../../../api/httpClient';
import Cookies from 'js-cookie';
import { v4 as uuid } from "uuid";
import { Trash2, CreditCard, Wallet, Truck, DollarSign, MapPin } from "lucide-react";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { useDispatch, useSelector } from "react-redux";
import { incrementAuditRefresh } from "../../../../redux/slices/invoiceSlice";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL+"/api";



const NewCarrierInvoice = ({ title, shipments: preselected = [], draftInvoice = null, onSave }) => {
  const dispatch = useDispatch();  
  const listViewState = useSelector((s) => s.invoices.listViewState || {});
  const chargePageSize =
    listViewState["my-invoices"]?.itemsPerPage ||
    listViewState["charges-audit"]?.itemsPerPage ||
    50;
  
  const [invoiceData, setInvoiceData] = useState({
    'Invoice Number': { type: 'text', value: '' },
    'Invoice Date':   { type: 'date',  value: new Date().toISOString().split('T')[0], disabled: true, readonly: true },
    'Payment Terms':  { type: 'text',  value: '', disabled: true,readonly: true  },
    'Currency':       { type: 'dropdown', value: 'USD', options: ['USD','EUR','GBP'] },
    'Invoice Total':          { type: 'number', value: '',readonly: true },
    'Invoice Status': { type: 'text',  value: 'Pending',readonly: true }
  });

  
  const formatMoney = (amount, currency) => {
    if (amount == null || amount === "") return null;
    
    const n = Number(amount);
    if (Number.isNaN(n)) return null;
  
    const safe = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const code = /^[A-Z]{3}$/.test(safe) ? safe : "USD";
  
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${code}`;
    }
  };
  const [approvalInfo, setApprovalInfo] = useState({
    approvedBy: '',
    approvalDate: '',
    rejectedBy: '',
    rejectionDate: '',
    createdBy: '',
    createdDate: '',
  });

  const INVALID_CHARS_REGEX = /[,~;]/;


// LGX-1107: shipment numbers repeated across blocks (each with its own BASE/FSC copy) inflate invoice charges.
const getDuplicateShipmentNumbers = (shipmentBlocks) => {
  const counts = {};
  (shipmentBlocks || []).forEach(s => {
    const sn = (s.shipmentNumber || '').trim();
    if (sn) counts[sn] = (counts[sn] || 0) + 1;
  });
  return Object.keys(counts).filter(sn => counts[sn] > 1);
};

const EMPTY_SHIPMENT = {
  shipmentNumber:     '',
  shipmentOrigin:     '',
  shipmentDestination:'',
  equipmentNumber:    '',
  shipmentCharge:     '',
  charges: [{
    chargeAmount:      '',
    chargeCurrency:    'USD',
    chargeType:        '',
    chargeCode:        '',
    chargeName:        '',
    chargeAuditStatus: '',
    isAutoPopulated:   false
  }],
  estimatedCharges: [{
    chargeAmount:      '',
    chargeCurrency:    'USD',
    chargeType:        'Base Charge',
    chargeCode:        'BAS',
    chargeName:        '', 
    chargeAuditStatus: '',
    isAutoPopulated:   true
  }],
  shipToInfo: [{
    id: uuid(),
    shipToCompanyName: '',
    shipToAddress:     '',
    shipToCity:        '',
    shipToState:       '',
    shipToZip:         '',
    shipToCountry:     '',
    shipToPhone:       '',
    shipToEmail:       '',
    shipToFax:         ''
  }]
};

  const [formData, setFormData] = useState({
    carrierOfficeCode: Cookies.get("officeCodes"),
    shipmentOfficeCode: '',
    billToOfficeCode: "",
    shipToOfficeCode: "",
    payToOfficeCode: "",
    billToCompanyName:'', billToAddress:'', billToCity:'', billToState:'', billToZip:'', billToCountry:'',
    billToPhone:'', billToEmail:'', billToFax:'',
    payToCompanyName:'', payToAddress:'', payToCity:'', payToState:'', payToZip:'', payToCountry:'',
    payToPhone:'', payToEmail:'', payToFax:'',
    invoiceType: '',
    parentShipmentNumber: '',
  });
  const handleFormChange = (field,value) =>
    setFormData(prev => ({ ...prev, [field]: value }));
 

    const handlePayToChange = (field, value) => {
      const sanitized = value.replace(INVALID_CHARS_REGEX, '');
      setFormData(prev => ({ ...prev, [field]: sanitized }));
    };

  const [fadeOut, setFadeOut] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState('');
  const [autoFieldsPopulated, setAutoFieldsPopulated] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [codeMappings, setCodeMappings] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const CHARGE_DISPLAY_NAMES = { FSC: 'Fuel Surcharge', BASE: 'Base Charge' };
  const getChargeDisplayName = (code, chargeName) =>
    codeMappings.find(cm => cm.code === code)?.description ||
    CHARGE_DISPLAY_NAMES[code] ||
    (chargeName && chargeName !== code ? chargeName : null);
  const isRejectedInvoice = draftInvoice && draftInvoice.invoiceStatus === "Rejected";

  const showAuditInfo =
  invoiceData["Invoice Status"]?.value === "Rejected" ||
  invoiceData["Invoice Status"]?.value === "Draft";

  const isDraftOrRejectedInvoice = ["Draft", "Rejected"].includes(
    invoiceData["Invoice Status"]?.value
  );
  

  const fetchPaymentTerms = async (billToOfficeCode) => {
    if (!billToOfficeCode) return;
  
    const token = Cookies.get("authToken");
  
    try {
      const resp = await httpClient.get(
        `${API_BASE_URL}/code-mapping/type/payment_terms`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": billToOfficeCode,
          },
        }
      );
  
      if (!Array.isArray(resp.data) || resp.data.length === 0) return;
  
      const mapping = resp.data[0];

            const term = mapping.description || mapping.code || "";
  
      setInvoiceData(prev => ({
        ...prev,
        "Payment Terms": {
          ...prev["Payment Terms"],
          value: term,
          disabled: true,
          readonly: true,
        },
      }));
    } catch (err) {
      console.error("Failed to load payment terms:", err);
    }
  };
  

   const [shipments, setShipments] = useState(
        preselected.length > 0
        ? preselected.map(s => ({ ...EMPTY_SHIPMENT, ...s }))
             : [{ ...EMPTY_SHIPMENT }]);


             const handleAddShipTo = (shipmentIndex) => {
              setShipments(prev => prev.map((s, idx) => {
                if (idx !== shipmentIndex) return s;
                return {
                  ...s,
                  shipToInfo: [
                    ...s.shipToInfo,
                    {
                      id: uuid(),
                      shipToCompanyName: '',
                      shipToAddress:     '',
                      shipToCity:        '',
                      shipToState:       '',
                      shipToZip:         '',
                      shipToCountry:     '',
                      shipToPhone:       '',
                      shipToEmail:       '',
                      shipToFax:         ''
                    }
                  ]
                };
              }));
            };

            const rebuildOrigChargeMap = async (shipmentNumbers, useOfficeCode = null) => {
              const newMap = {};
              const codeToUse = useOfficeCode || officeCode;
              
              await Promise.all(
                shipmentNumbers.map(async (sn) => {
                  if (!sn) return;
                  
                  try {
                    const { data } = await httpClient.get(
                      `${API_BASE_URL}/shipments/detailed-shipment/${sn}`,
                      {
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                          "Office-Code": codeToUse,
                        },
                      }
                    );
                    
                    const baseline = {};
                    [...(data.charges || []), ...(data.estimatedCharges || [])].forEach(c => {
                      const code = c.code;
                      const amount = parseFloat(c.amount) || 0;
                      if (!baseline[code]) baseline[code] = [];
                      baseline[code].push(amount);
                    });
                    
                    newMap[sn] = baseline;
                  } catch (err) {
                    console.error(`Failed to fetch shipment ${sn}:`, err);
                  }
                })
              );
              
              setOrigChargeMap(prev => ({ ...prev, ...newMap }));
            };
            
            const handleRemoveShipTo = (shipmentIndex, shipToId) => {
              setShipments(prev => prev.map((s, idx) => {
                if (idx !== shipmentIndex) return s;
                return {
                  ...s,
                  shipToInfo: s.shipToInfo.length > 1 
                    ? s.shipToInfo.filter(st => st.id !== shipToId)
                    : s.shipToInfo
                };
              }));
            };
            
            const handleShipToChange = (shipmentIndex, shipToId, field, value) => {
              setShipments(prev => prev.map((s, idx) => {
                if (idx !== shipmentIndex) return s;
                return {
                  ...s,
                  shipToInfo: s.shipToInfo.map(st => 
                    st.id === shipToId ? { ...st, [field]: value } : st
                  )
                };
              }));
            };


            useEffect(() => {
              if (preselected.length > 0 && preselected[0].uniqueId) {
                setEditingInvoiceId(preselected[0].uniqueId);
              }
            }, [preselected]);

useEffect(() => {

  if (!draftInvoice) {
        return;
  }
  
  const loadFullDraft = async () => {
    try {
      const token = Cookies.get('authToken');

      const { data } = await httpClient.get(
        `${API_BASE_URL}/invoices/${draftInvoice.uniqueId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Office-Code': draftInvoice.payToOfficeCode
          }
        }
      );
      
        setEditingInvoiceId(data.uniqueId);
  
  setEditingInvoiceId(data.uniqueId);
      
  setInvoiceData({
    'Invoice Number': { ...invoiceData['Invoice Number'], value: data.invoiceNumber },
    'Invoice Date':   { ...invoiceData['Invoice Date'],   value: data.invoiceDate, disabled: true, readonly: true },
    'Payment Terms':  { type: 'text', value: data.paymentTerms || 'Net 30', disabled: true,readonly: true  },    'Currency':       { ...invoiceData['Currency'],       value: data.currency },
    'Invoice Total':          { ...invoiceData['Invoice Total'],          value: data.total, readonly: true  },
    'Invoice Status': { ...invoiceData['Invoice Status'], value: data.invoiceStatus, readonly: true  },
  
  });

  setApprovalInfo({
    approvedBy: data.approvedBy || '',
    approvalDate: data.approvalDate
      ? new Date(data.approvalDate).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : '',
    rejectedBy: data.rejectedBy || '',
    rejectionDate: data.rejectedDate
      ? new Date(data.rejectedDate).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : '',
    createdBy: data.createdBy || '',
    createdDate: data.createdDate
      ? new Date(data.createdDate).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : '',
  });

  setFormData(prev => ({
    ...prev,
    shipmentOfficeCode:  data.billToOfficeCode,
    billToOfficeCode:    data.billToOfficeCode,
    payToOfficeCode:     data.payToOfficeCode,
    shipToOfficeCode:    data.shipToOfficeCode,
    billToCompanyName:   data.billToCompanyName,
    billToAddress:       data.billToAddress,
    billToCity:          data.billToCity,
    billToState:         data.billToState,
    billToZip:           data.billToZip,
    billToCountry:       data.billToCountry,
    billToPhone:         data.billToPhone,
    billToEmail:         data.billToEmail,
    billToFax:           data.billToFax,
    payToCompanyName:    data.payToCompanyName,
    payToAddress:        data.payToAddress,
    payToCity:           data.payToCity,
    payToState:          data.payToState,
    payToZip:            data.payToZip,
    payToCountry:        data.payToCountry,
    payToPhone:          data.payToPhone,
    payToEmail:          data.payToEmail,
    payToFax:            data.payToFax,
  }));

  const invShipments = Array.isArray(data.shipments) ? data.shipments : [];
  if (data.invoiceType === 'PARENT') {
    const map = {};
    invShipments.forEach(s => {
      const byCode = {};
      (s.charges || []).forEach(c => {
        const code = c.chargeCode;
        const amt = parseFloat(c.chargeAmount) || 0;
        if (!byCode[code]) byCode[code] = [];
        byCode[code].push(amt);
      });
      map[s.shipmentNumber] = byCode;
    });
    setOrigChargeMap(map);
  } else {
    await rebuildOrigChargeMap(invShipments.map(s => s.shipmentNumber), data.billToOfficeCode);
  }

  if (invShipments.length > 0) {
    setShipments(
      invShipments.map(s => {
        let shipToInfo = [];

        if (Array.isArray(s.shipTos) && s.shipTos.length > 0) {
          shipToInfo = s.shipTos.map(st => ({
            id: uuid(),
            shipToCompanyName: st.companyName || '',
            shipToAddress:     st.address || '',
            shipToCity:        st.city || '',
            shipToState:       st.state || '',
            shipToZip:         st.zip || '',
            shipToCountry:     st.country || '',
            shipToPhone:       st.phone || '',
            shipToEmail:       st.email || '',
            shipToFax:         st.fax || ''
          }));
        } else {
                    shipToInfo = [{
            id: uuid(),
            shipToCompanyName: '',
            shipToAddress:     '',
            shipToCity:        '',
            shipToState:       '',
            shipToZip:         '',
            shipToCountry:     '',
            shipToPhone:       '',
            shipToEmail:       '',
            shipToFax:         ''
          }];
        }


        return {
          ...EMPTY_SHIPMENT,
          shipmentNumber:      s.shipmentNumber || '',
          shipmentOrigin:      s.shipmentOrigin || '',
          shipmentDestination: s.shipmentDestination || '',
          equipmentNumber:     s.equipmentNumber || '',
          shipmentCharge:      parseFloat(s.shipmentCharge).toFixed(2) || '0.00',
          charges: (Array.isArray(s.charges) && s.charges.length)
            ? s.charges.map(c => ({ 
                id: c.chargeId || uuid(), 
                chargeAmount:      c.chargeAmount || 0,
                chargeCurrency:    c.chargeCurrency || 'USD',
                chargeType:        c.chargeType || 'COST',
                chargeCode:        c.chargeCode || '',
                chargeName: c.chargeName||'',
                chargeAuditStatus: c.chargeAuditStatus || '',
                discrepancyStatus: c.discrepancyStatus || '',
                discrepancyAmount: c.discrepancyAmount || 0,
                isAutoPopulated:   false
              }))
            : [],
          shipToInfo
        };
      })
    );
  } else {
    setShipments([{ ...EMPTY_SHIPMENT }]);
  }
} catch (err) {
  console.error("❌ Error loading full draft:", err);
}
};

loadFullDraft();
  
}, [draftInvoice]);
useEffect(() => {
  if (!preselected.length) {
    setShipments([{ ...EMPTY_SHIPMENT }]);
    setOrigChargeMap({});
    return;
  }

  
  const built = preselected.map(s => {
    const actual = (s.charges || []).map(c => {
      const mapped = {
        id: uuid(),
        chargeAmount: c.chargeAmount || c.amount,
        chargeCurrency: c.chargeCurrency || c.currency,
        chargeType: c.chargeType || c.type,
        chargeCode: c.chargeCode || c.code,
        chargeName: c.chargeName || c.name || '',
        isAutoPopulated: false,
      };
            return mapped;
    });

    const estimated = (s.estimatedCharges || []).map(c => {
      const mapped = {
        id: uuid(),
        chargeAmount: c.chargeAmount || c.amount,
        chargeCurrency: c.chargeCurrency || c.currency,
        chargeType: c.chargeType || c.chargeName || c.type,
        chargeCode: c.chargeCode || c.code,
        chargeName: c.chargeName || c.name || '',
        isAutoPopulated: true,
      };
            return mapped;
    });

    const shipToInfo = Array.isArray(s.shipTos) && s.shipTos.length > 0
      ? s.shipTos.map(st => ({
          id: uuid(),
          shipToCompanyName: st.companyName || st.shipToCompanyName || '',
          shipToAddress: st.address || st.shipToAddress || '',
          shipToCity: st.city || st.shipToCity || '',
          shipToState: st.state || st.shipToState || '',
          shipToZip: st.zip || st.shipToZip || '',
          shipToCountry: st.country || st.shipToCountry || '',
          shipToPhone: st.phone || st.shipToPhone || '',
          shipToEmail: st.email || st.shipToEmail || '',
          shipToFax: st.fax || st.shipToFax || ''
        }))
      : [{
          id: uuid(),
          shipToCompanyName: s.shipToCompanyName || '',
          shipToAddress: s.shipToAddress || '',
          shipToCity: s.shipToCity || '',
          shipToState: s.shipToState || '',
          shipToZip: s.shipToZip || '',
          shipToCountry: s.shipToCountry || '',
          shipToPhone: s.shipToPhone || '',
          shipToEmail: s.shipToEmail || '',
          shipToFax: s.shipToFax || ''
        }];

    return {
      ...EMPTY_SHIPMENT,
      ...s,
      imported: true,
      charges: [
        ...actual,
        ...estimated
      ],
      shipToInfo
    };
  });

  setShipments(built);

  preselected.forEach(s => {
    const sn = s.shipmentNumber;
    if (!sn) return;
    
    console.log(`🔵 Building baseline for ${sn}`);
    const baseline = {};
    (s.charges || []).forEach(c => {
      const code = c.chargeCode || c.code;
      const amount = parseFloat(c.chargeAmount || c.amount) || 0;
      if (!baseline[code]) baseline[code] = [];
      baseline[code].push(amount);
      console.log(` 📊 Baseline actual [${code}] += ${amount}`);
    });

    (s.estimatedCharges || []).forEach(c => {
      const code = c.chargeCode || c.code;
      const amount = parseFloat(c.chargeAmount || c.amount) || 0;
      if (!baseline[code]) baseline[code] = [];
      baseline[code].push(amount);
      console.log(` 📊 Baseline estimated [${code}] += ${amount}`);
    });

        
    setOrigChargeMap(prev => {
      const updated = { ...prev, [sn]: baseline };
            return updated;
    });
  });

  const first = built[0];
  
  setFormData(f => ({
    ...f,
    shipmentOfficeCode: first.shipmentOfficeCode || f.shipmentOfficeCode,
    billToOfficeCode: first.billToOfficeCode || f.billToOfficeCode,
    shipToOfficeCode: first.shipToOfficeCode || f.shipToOfficeCode,
    payToOfficeCode: first.payToOfficeCode || f.payToOfficeCode,
    billToCompanyName: first.billToCompanyName || f.billToCompanyName,
    billToAddress: first.billToAddress || f.billToAddress,
    billToCity: first.billToCity || f.billToCity,
    billToState: first.billToState || f.billToState,
    billToZip: first.billToZip || f.billToZip,
    billToCountry: first.billToCountry || f.billToCountry,
    billToPhone: first.billToPhone || f.billToPhone,
    billToEmail: first.billToEmail || f.billToEmail,
    billToFax: first.billToFax || f.billToFax,
    payToCompanyName: first.payToCompanyName || f.payToCompanyName,
    payToAddress: first.payToAddress || f.payToAddress,
    payToCity: first.payToCity || f.payToCity,
    payToState: first.payToState || f.payToState,
    payToZip: first.payToZip || f.payToZip,
    payToCountry: first.payToCountry || f.payToCountry,
    payToPhone: first.payToPhone || f.payToPhone,
    payToEmail: first.payToEmail || f.payToEmail,
    payToFax: first.payToFax || f.payToFax,
    invoiceType: first.invoiceType || f.invoiceType,
    parentShipmentNumber: first.parentShipmentNumber || f.parentShipmentNumber,
  }));

}, [preselected]);

useEffect(() => {
  if (formData.billToOfficeCode && !draftInvoice) {
        fetchPaymentTerms(formData.billToOfficeCode);
  }
}, [formData.billToOfficeCode, draftInvoice]);



  // useEffect(() => {
  //   if (!preselected.length) {
  //     setShipments([{ ...EMPTY_SHIPMENT }]);
  //     setOrigChargeMap({});
  //     return;
  //   }



            
  const handleDeleteInvoice = async () => {
    const invoiceToDelete = draftInvoice || {
      uniqueId: editingInvoiceId,
      billToOfficeCode: formData.billToOfficeCode,
      shipments: shipments.map(s => ({ shipmentNumber: s.shipmentNumber }))
    };
  
    try {
      await httpClient.delete(
        `${API_BASE_URL}/invoices/delete-invoice/${invoiceToDelete.uniqueId}`,
        {
          headers: {
            "Office-Code": invoiceToDelete.billToOfficeCode,
            Authorization: `Bearer ${Cookies.get("authToken")}`,
          },
        }
      );

  
      notify.success({
        title: "Invoice deleted",
        message: `${draftInvoice ? 'Draft' : 'Rejected'} invoice has been permanently deleted.`,
      });
      
      setShowDeleteConfirm(false);
      onSave?.();
    } catch (err) {
      console.error("Delete failed:", err);
      notify.error({
        title: "Delete failed",
        message: err?.response?.data?.message || "Could not delete invoice.",
      });
      setShowDeleteConfirm(false);
    }
  };;
  const [origChargeMap, setOrigChargeMap] = useState({});

  const calculateShipmentTotal = (charges) => {
    if (!Array.isArray(charges) || charges.length === 0) return 0;
    
    const total = charges.reduce((sum, charge) => {
      const amount = parseFloat(charge.chargeAmount) || 0;
      return sum + amount;
    }, 0);
    
    return parseFloat(total.toFixed(2)); 
  };


  const calculateInvoiceTotal = (shipments) => {
    if (!Array.isArray(shipments) || shipments.length === 0) return 0;
    
    const total = shipments.reduce((sum, shipment) => {
      const carrierCharges = shipment.charges.filter(c => !c.isAutoPopulated);
      const shipmentTotal = calculateShipmentTotal(carrierCharges);
      return sum + shipmentTotal;
    }, 0);
    
    return parseFloat(total.toFixed(2));
  };

  const getEstimatedAmount = (shipmentNumber, chargeCode) => {
    const amounts = origChargeMap?.[shipmentNumber]?.[chargeCode];
    if (!Array.isArray(amounts) || amounts.length === 0) return null;
    if (formData.invoiceType === 'PARENT') {
      return amounts.reduce((s, a) => s + a, 0);
    }
    return amounts[0];
  };
  

  const handleShipmentChange = (i,f,v) =>
    setShipments(prev => prev.map((s,idx) => idx===i ? { ...s, [f]: v } : s ));

    
  const handleChargeChange = (shipmentIndex, chargeId, field, value) => {
    setShipments(prev =>
      prev.map((shipment, si) => {
        if (si !== shipmentIndex) return shipment;
  
        // 🔁 keep the duplicate FSC/BAS/BASE guard
        if (field === 'chargeCode' && (value === 'FSC' || value === 'BAS' || value === 'BASE')) {
          const existingCount = shipment.charges.filter(
            c => c.id !== chargeId && c.chargeCode === value && !c.isAutoPopulated
          ).length;
          
          if (existingCount > 0) {
            notify.warning({
              title: "Duplicate charge code",
              message: `Only one ${value} charge is allowed per shipment.`
            });
            return shipment;
          }
        }

        const updatedCharges = shipment.charges.map(c => {
          if (c.id !== chargeId) return c;
  
          const clearAuto = field === "chargeCode" || field === "chargeType";
  
          // if (field === "chargeCode") {
          //   const matched = codeMappings.find(cm => cm.code === value);
          //   const resolvedName =
          //     (matched && (matched.description || matched.name)) ||
          //     c.chargeName ||
          //     "";
  
          //   return {
          //     ...c,
          //     chargeCode: value,
          //     chargeName: resolvedName,
          //     isAutoPopulated: false,
          //   };
          // }

          // ⭐ When chargeCode changes, also auto-set chargeName from codeMappings
          if (field === "chargeCode") {
            const matched = codeMappings.find(cm => cm.code === value);

            const resolvedName =
              (matched && (matched.description || matched.name)) ||
              c.chargeName ||
              "";

            return {
              ...c,
              chargeCode: value,
              chargeName: resolvedName,
              isAutoPopulated: false,
            };
          }



// ⭐ When chargeName changes, also auto-set chargeCode from codeMappings
if (field === "chargeName") {
  const matched = codeMappings.find(
    cm => cm.description === value || cm.name === value
  );
  const resolvedCode = (matched && matched.code) || c.chargeCode || "";

  return {
    ...c,
    chargeName: value,
    chargeCode: resolvedCode,
    isAutoPopulated: false,
  };
}
  
          // Default behavior for all other fields
          return {
            ...c,
            [field]: value,
            isAutoPopulated: clearAuto ? false : c.isAutoPopulated,
          };
        });
  
        return {
          ...shipment,
          charges: updatedCharges,
        };
      })
    );
  };
  
const emptyChargeTemplate = {
  chargeCode:     "",
  chargeAmount:   "",
  chargeCurrency: "USD",
  chargeName:     "",
  shipmentStop: null,
  isAutoPopulated: false,
};

  const handleAddShipmentBlock = () =>
    setShipments(prev => [...prev, { ...EMPTY_SHIPMENT }]);
  const handleRemoveShipmentBlock = i =>
    shipments.length>1 && setShipments(prev => prev.filter((_,idx) => idx!==i));


  const handleAddCharge = i => {
    setShipments(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      
      const updatedCharges = [
        ...s.charges, 
        { id: uuid(), ...emptyChargeTemplate }
      ];
      
      //const newTotal = calculateShipmentTotal(updatedCharges);
      
      return { 
        ...s, 
        charges: updatedCharges,
        //shipmentCharge: newTotal.toFixed(2)
      };
    }));
  };
   

    const handleRemoveCharge = (shipmentIndex, chargeId) => {
      setShipments(prev =>
        prev.map((shipment, si) => {
          if (si !== shipmentIndex) return shipment;
          
          const updatedCharges = shipment.charges.length > 1
            ? shipment.charges.filter(c => c.id !== chargeId)
            : shipment.charges;
          
          //const newTotal = calculateShipmentTotal(updatedCharges);
          
          return {
            ...shipment,
            charges: updatedCharges,
            //shipmentCharge: newTotal.toFixed(2)
          };
        })
      );
    };

    useEffect(() => {
      const total = calculateInvoiceTotal(shipments);
      setInvoiceData(prev => ({
        ...prev,
        'Invoice Total': { ...prev['Invoice Total'],   value: formatMoney(total, prev['Currency'].value) , readonly: true }
      }));
    }, [shipments]);



  const MODE_OPTIONS = ['Shipment Number', 'Equipment Number','Drafts','Rejected','Templates'];
  const [dropdownMode, setDropdownMode] = useState(MODE_OPTIONS[0]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedSearchValue, setSelectedSearchValue] = useState('');
  const [rejectedInvoices, setRejectedInvoices] = useState([]);


  const [allShipments, setAllShipments]       = useState([]);
  const savedTemplates                        = ['Template 1','Template 2','Template 3'];
  const [drafts, setDrafts]                   = useState([]);
  useEffect(() => {
    if (allShipments.length > 0) {
      handleModeChange(dropdownMode);
    }
  }, [allShipments]);
  
  

  const token = Cookies.get("authToken");

  const officeCode = Cookies.get("officeCode");
  //const officeCode = "DIAMOND-META";

  useEffect(() => {

        httpClient.post(
      `${API_BASE_URL}/shipments/not-invoiced`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Office-Code": officeCode,


          
        },
      }
    )
    .then(r => {
      const content = Array.isArray(r.data?.content) ? r.data.content : [];
      const shipmentsOnly = content.filter(x => x && typeof x === 'object' && !Array.isArray(x));      
            setAllShipments(shipmentsOnly);
    })
    .catch(e => console.error('Error loading shipments:', e));
  }, [token, officeCode]);

  //const shipperCode = data.shipperOfficeCode?.partyOfficeCode;


  const shipperOfficeCode = formData.shipmentOfficeCode;
  
  useEffect(() => {
    // if (!shipperOfficeCode || shipperOfficeCode === '') {
    //   setCodeMappings([]); // Clear mappings if no shipment selected
    //   return; ${process.env.REACT_APP_API_BASE_URL}/api/code-mapping/type/accessorial
    // }
    httpClient
      .get(`${API_BASE_URL}/code-mapping/type/accessorial`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Office-Code": shipperOfficeCode, 
        }
      })
      .then(resp => setCodeMappings(resp.data))
      .catch(err => console.error("Failed to load code mappings:", err));
  }, [token, shipperOfficeCode]);
  

  

  const handleSelectSearchItem = async (selected) => {
    setSelectedShipment(""); 
    setEditingInvoiceId(null);
  
    const mode = dropdownMode;

    let id, display;
    if (dropdownMode === 'Drafts'|| dropdownMode === 'Rejected') {
      [id, display] = selected.split('|');
      setSelectedSearchValue(display); 
      setEditingInvoiceId(id);                         
    } else {
      setSelectedSearchValue(selected);
    }
  
    if (mode === "Shipment Number") {
      // PARENT invoices are pre-filled with REVENUE-only charges; skip auto-populate.
      if (formData.invoiceType === "PARENT") return;

      const sn = selected.split('|')[0].trim();
      setSelectedShipment(sn);
      const selectedShipmentObj = allShipments.find(s => s.shipmentNumber === sn);
      const officeCodeToUse = selectedShipmentObj?.officeCode || Cookies.get("officeCode");
    
          
      try {
        const { data } = await httpClient.get(
          `${API_BASE_URL}/shipments/detailed-shipment/${sn}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "Office-Code": officeCodeToUse,
            },
          }
        );
    
            
        const shipperCode = data.shipperOfficeCode?.partyOfficeCode;
        const carrierCode = data.carrierOfficeCode?.partyOfficeCode;
            
        const baseline = {};
        
                                
        (data.charges || []).forEach(c => {
          const code = c.code;
          const amount = parseFloat(c.amount) || 0;
          if (!baseline[code]) baseline[code] = [];
          baseline[code].push(amount);
          console.log(`  📊 Baseline actual [${code}] += ${amount}`);
        });

        (data.estimatedCharges || []).forEach(c => {
          const code = c.code;
          const amount = parseFloat(c.amount) || 0;
          if (!baseline[code]) baseline[code] = [];
          baseline[code].push(amount);
          console.log(`  📊 Baseline estimated [${code}] += ${amount}`);
        });

        console.log(`🔵 Final baseline for ${sn}:`, baseline);
        
                
        setOrigChargeMap(prev => {
          const updated = { ...prev, [sn]: baseline };
                    return updated;
        });
    
        setFormData(prev => ({
          ...prev,
          officeCode: data.officeCode || prev.officeCode,
          billToOfficeCode: data.shipperOfficeCode?.partyOfficeCode || "",
          shipToOfficeCode: data.receiverOfficeCode?.partyOfficeCode || "",
          shipmentOfficeCode: data.shipperOfficeCode?.partyOfficeCode,
          payToOfficeCode: data.carrierOfficeCode?.partyOfficeCode || "",
          billToCompanyName: data.shipperOfficeCode?.name || '',
          billToAddress: data.shipperOfficeCode?.addressLine1 || '',
          billToCity: data.shipperOfficeCode?.city || '',
          billToState: data.shipperOfficeCode?.state || '',
          billToZip: data.shipperOfficeCode?.zipCode || '',
          billToCountry: data.shipperOfficeCode?.country || '',
          billToPhone: data.shipperOfficeCode?.contactPhone || '',
          billToEmail: data.shipperOfficeCode?.contactEmail || '',
          billToFax: data.shipperOfficeCode?.contactFax || '',
          payToCompanyName: data.carrierOfficeCode?.name || '',
          payToAddress: data.carrierOfficeCode?.addressLine1 || '',
          payToCity: data.carrierOfficeCode?.city || '',
          payToState: data.carrierOfficeCode?.state || '',
          payToZip: data.carrierOfficeCode?.zipCode || '',
          payToCountry: data.carrierOfficeCode?.country || '',
          payToPhone: data.carrierOfficeCode?.contactPhone || '',
          payToEmail: data.carrierOfficeCode?.contactEmail || '',
          payToFax: data.carrierOfficeCode?.contactFax || '',
        }));

        const billToCode = data.shipperOfficeCode?.partyOfficeCode;
if (billToCode) {
  fetchPaymentTerms(billToCode);
}

    
        setInvoiceData(prev => ({
          ...prev,
          'Invoice Total': { ...prev['Invoice Total'], value: data.totalCharge ? parseFloat(data.totalCharge).toFixed(2) : '', readonly: true },
          'Payment Terms': { ...prev['Payment Terms'], value: prev['Payment Terms'].value, disabled: true, readonly: true }        }));
    
        const receiver = data.receiverOfficeCode || {};

        const actualCharges = (data.charges || []).map(ch => ({
          id: uuid(),
          chargeType:      ch.chargeType || ch.type,
          chargeCode:      ch.chargeCode || ch.code || "",
          chargeName:      ch.chargeName || ch.name || "",
          chargeAmount:    ch.amount,
          chargeCurrency:  ch.currency || 'USD',
          isAutoPopulated: false
        }));

        const estimatedCharges = (data.estimatedCharges || []).map(ec => ({
          id: uuid(),
          chargeType:      ec.chargeType || ec.chargeName || ec.type,
 chargeCode:      ec.chargeCode || ec.code || "",
 chargeAmount:    ec.amount,
 chargeName:      ec.chargeName || ec.name || "",
          chargeCurrency:  ec.currency || 'USD',
          isAutoPopulated: true
        }));
    
                            
        const dropOffStops = (data.shipmentStops || [])
          .filter(stop => stop.stopType === 'DROP_OFF')
          .map(stop => ({
            id: uuid(),
            shipToCompanyName: stop.stopName || '',
            shipToAddress:     stop.addressLine1 || '',
            shipToCity:        stop.city || '',
            shipToState:       stop.state || '',
            shipToZip:         stop.zipCode || '',
            shipToCountry:     stop.country || '',
            shipToPhone:       '',  
            shipToEmail:       '',  
            shipToFax:         ''   
          }));
    
        const shipToInfoArray = dropOffStops.length > 0 
          ? dropOffStops 
          : [{
              id: uuid(),
              shipToCompanyName: data.receiverOfficeCode?.name || '',
              shipToAddress:     data.receiverOfficeCode?.addressLine1 || '',
              shipToCity:        data.receiverOfficeCode?.city || '',
              shipToState:       data.receiverOfficeCode?.state || '',
              shipToZip:         data.receiverOfficeCode?.zipCode || '',
              shipToCountry:     data.receiverOfficeCode?.country || '',
              shipToPhone:       data.receiverOfficeCode?.contactPhone || '',
              shipToEmail:       data.receiverOfficeCode?.contactEmail || '',
              shipToFax:         data.receiverOfficeCode?.contactFax || ''
            }];
    
        const newShipment = {
          shipmentNumber:    data.shipmentNumber || '',
          shipmentOrigin:    data.portOfOrigin || '',
          shipmentDestination: data.portOfDestination || '',
          equipmentNumber:   data.equipmentNumber || '',
          shipmentCharge:    data.totalCharge ? parseFloat(data.totalCharge).toFixed(2) : '',
          charges: [
            ...actualCharges,
            ...estimatedCharges
          ],
          shipToInfo: shipToInfoArray
        };
    
            
        setShipments(prev => {
          const nonEmpty = prev.filter(s => Boolean(s.shipmentNumber));
          return [...nonEmpty, newShipment];
        });
    
        setAutoFieldsPopulated(true);
    
      } catch (err) {
        console.error("Error loading shipment:", err);
      }
    }

    else if (dropdownMode === 'Drafts') {
      try {
        const token      = Cookies.get('authToken');

        const officeCode = Cookies.get("officeCode");


        const { data } = await httpClient.get(
          `${API_BASE_URL}/invoices/${id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization:  `Bearer ${token}`,
              'Office-Code':  officeCode
            }
          }
        );


        setInvoiceData({
          'Invoice Number': { ...invoiceData['Invoice Number'], value: data.invoiceNumber },
          'Invoice Date':   { ...invoiceData['Invoice Date'],   value: data.invoiceDate, disabled: true, readonly: true },
          'Payment Terms':  { ...invoiceData['Payment Terms'],  value: data.paymentTerms },
          'Currency':       { ...invoiceData['Currency'],       value: data.currency },
          'Invoice Total':          { ...invoiceData['Invoice Total'],          value: data.total },
          'Invoice Status': { ...invoiceData['Invoice Status'], value: data.invoiceStatus },
     
        });

        setApprovalInfo({
          approvedBy: data.approvedBy || '',
          approvalDate: data.approvalDate
            ? new Date(data.approvalDate).toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : '',
          rejectedBy: data.rejectedBy || '',
          rejectionDate: data.rejectedDate
            ? new Date(data.rejectedDate).toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : '',
          createdBy: data.createdBy || '',
          createdDate: data.createdDate
            ? new Date(data.createdDate).toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : '',
        });

        setFormData(prev => ({
          ...prev,
          shipmentOfficeCode: data.billToOfficeCode,
          billToOfficeCode:    data.billToOfficeCode,
          payToOfficeCode:     data.payToOfficeCode,
          shipToOfficeCode:    data.shipToOfficeCode,
          billToCompanyName:   data.billToCompanyName,
          billToAddress:       data.billToAddress,
          billToCity:          data.billToCity,
          billToState:         data.billToState,
          billToZip:           data.billToZip,
          billToCountry:       data.billToCountry,
          billToPhone:         data.billToPhone,
          billToEmail:         data.billToEmail,
          billToFax:           data.billToFax,
          payToCompanyName:    data.payToCompanyName,
          payToAddress:        data.payToAddress,
          payToCity:           data.payToCity,
          payToState:          data.payToState,
          payToZip:            data.payToZip,
          payToCountry:        data.payToCountry,
          payToPhone:          data.payToPhone,
          payToEmail:          data.payToEmail,
          payToFax:            data.payToFax,
        }));

        

        const invShipments = Array.isArray(data.shipments) ? data.shipments : [];
        if (data.invoiceType === 'PARENT') {
          const map = {};
          invShipments.forEach(s => {
            const byCode = {};
            (s.charges || []).forEach(c => {
              const code = c.chargeCode;
              const amt = parseFloat(c.chargeAmount) || 0;
              if (!byCode[code]) byCode[code] = [];
              byCode[code].push(amt);
            });
            map[s.shipmentNumber] = byCode;
          });
          setOrigChargeMap(map);
        } else {
          await rebuildOrigChargeMap(invShipments.map(s => s.shipmentNumber), data.billToOfficeCode);
        }
        setShipments(
          invShipments.length
            ? invShipments.map(s => {
                // Map shipTos from backend to shipToInfo for frontend
                let shipToInfo = [];
                
                if (Array.isArray(s.shipTos) && s.shipTos.length > 0) {
                  shipToInfo = s.shipTos.map(st => ({
                    id: uuid(),
                    shipToCompanyName: st.companyName || '',
                    shipToAddress:     st.address || '',
                    shipToCity:        st.city || '',
                    shipToState:       st.state || '',
                    shipToZip:         st.zip || '',
                    shipToCountry:     st.country || '',
                    shipToPhone:       st.phone || '',
                    shipToEmail:       st.email || '',
                    shipToFax:         st.fax || ''
                  }));
                } else {
                  shipToInfo = [{
                    id: uuid(),
                    shipToCompanyName: '',
                    shipToAddress:     '',
                    shipToCity:        '',
                    shipToState:       '',
                    shipToZip:         '',
                    shipToCountry:     '',
                    shipToPhone:       '',
                    shipToEmail:       '',
                    shipToFax:         ''
                  }];
                }
    
                return {
                  ...EMPTY_SHIPMENT,
                  shipmentNumber:      s.shipmentNumber || '',
                  shipmentOrigin:      s.shipmentOrigin || '',
                  shipmentDestination: s.shipmentDestination || '',
                  equipmentNumber:     s.equipmentNumber || '',
                  shipmentCharge: parseFloat(s.shipmentCharge).toFixed(2) || '0.0',
                  charges: (Array.isArray(s.charges) && s.charges.length)
                  ? s.charges.map(c => ({
                        id: c.chargeId || uuid(),
                        chargeAmount:      c.chargeAmount || 0,
                        chargeCurrency:    c.chargeCurrency || 'USD',
                        chargeType:        c.chargeType || 'COST',
                        chargeCode:        c.chargeCode || '',
                        chargeName:        c.chargeName || '',
                        chargeAuditStatus: c.chargeAuditStatus || '',
                        discrepancyStatus: c.discrepancyStatus || '',
                        discrepancyAmount: c.discrepancyAmount || 0,
                        isAutoPopulated:   false
                      }))
                  : [],
                  shipToInfo
                };
              })
            : [{ ...EMPTY_SHIPMENT }]
        );


        

      } catch (err) {
        console.error('Error loading draft invoice:', err);
      }
    }

    else if (dropdownMode === 'Rejected') {
            try {
              const { data } = await httpClient.get(
                `${API_BASE_URL}/invoices/${id}`,
               { headers: {
                  'Content-Type': 'application/json',
                  Authorization:  `Bearer ${token}`,
                  'Office-Code':  officeCode
                }}
              );
              setInvoiceData({
                'Invoice Number': { ...invoiceData['Invoice Number'], value: data.invoiceNumber },
                'Invoice Date':   { ...invoiceData['Invoice Date'],   value: data.invoiceDate, disabled: true, readonly: true },
                'Payment Terms':  { type: 'text', value: data.paymentTerms || 'Net 30', disabled: true, readonly: true  },
                'Currency':       { ...invoiceData['Currency'],       value: data.currency },
                'Invoice Total': { ...invoiceData['Invoice Total'], value: data.total ? parseFloat(data.total).toFixed(1) : '', readonly: true  },
                'Invoice Status': { ...invoiceData['Invoice Status'], value: data.invoiceStatus, readonly: true  },
               
              });
              setApprovalInfo({
                approvedBy: data.approvedBy || '',
                approvalDate: data.approvalDate
                  ? new Date(data.approvalDate).toLocaleString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : '',
                rejectedBy: data.rejectedBy || '',
                rejectionDate: data.rejectedDate
                  ? new Date(data.rejectedDate).toLocaleString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : '',
                createdBy: data.createdBy || '',
                createdDate: data.createdDate
                  ? new Date(data.createdDate).toLocaleString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : '',
              });
      
              setFormData(prev => ({
                ...prev,
                shipmentOfficeCode: data.billToOfficeCode,
                billToOfficeCode:    data.billToOfficeCode,
                payToOfficeCode:     data.payToOfficeCode,
                shipToOfficeCode:    data.shipToOfficeCode,
                billToCompanyName:   data.billToCompanyName,
                billToAddress:       data.billToAddress,
                billToCity:          data.billToCity,
                billToState:         data.billToState,
                billToZip:           data.billToZip,
                billToCountry:       data.billToCountry,
                billToPhone:         data.billToPhone,
                billToEmail:         data.billToEmail,
                billToFax:           data.billToFax,
                payToCompanyName:    data.payToCompanyName,
                payToAddress:        data.payToAddress,
                payToCity:           data.payToCity,
                payToState:          data.payToState,
                payToZip:            data.payToZip,
                payToCountry:        data.payToCountry,
                payToPhone:          data.payToPhone,
                payToEmail:          data.payToEmail,
                payToFax:            data.payToFax,
              }));
      
              const invShipments = Array.isArray(data.shipments) ? data.shipments : [];
              await rebuildOrigChargeMap(invShipments.map(s => s.shipmentNumber), data.billToOfficeCode);

if (invShipments.length > 0) {
  setShipments(
    invShipments.map(s => ({
      ...EMPTY_SHIPMENT,
      ...s,
      officeCode: s.officeCode,
      charges: Array.isArray(s.charges)
        ? s.charges.map(c => ({
            ...c,
            id: c.chargeId || uuid(),
          }))
        : []
    }))
  );
        } else {
          setShipments([{ ...EMPTY_SHIPMENT }]);
        }
      } catch (err) {
        console.error('Error loading rejected invoice:', err);
      }
    }

  
  };
  const [draftInvoices, setDraftInvoices] = useState([]);
  useEffect(() => {
    invoiceApi
      .detailedSearch({ query: '', invoiceStatus: 'Draft' }, 0, 100)
      .then(resp => setDraftInvoices(resp.content))
      .catch(err => console.error('Failed to load draft invoices:', err));
  }, []);

  useEffect(() => {
        invoiceApi
          .detailedSearch({ query: '', invoiceStatus: 'Rejected' }, 0, 100)
          .then(resp => setRejectedInvoices(resp.content))
          .catch(err => console.error('Failed to load rejected invoices:', err));
      }, []);

  const handleModeChange = (mode) => {
    setDropdownMode(mode);
    setSelectedSearchValue('');
    switch (mode) {
      case 'Shipment Number':
        setSearchOptions(allShipments.map(s =>
          `${s.shipmentNumber}|${s.portOfOrigin}|${s.portOfDestination}|${s.isoEquipmentCode}|${s.totalCharge}`
        ));
        break;
      case 'Equipment Number':
        setSearchOptions(allShipments.map(s =>
          `${s.equipmentNumber}|${s.shipmentNumber}|${s.portOfOrigin}|${s.portOfDestination}|${s.totalCharge}`
        ));
        break;
      case 'Templates':
        setSearchOptions(savedTemplates);
        break;
      case 'Drafts':
        setSearchOptions(draftInvoices.map(d => `${d.uniqueId}|${d.invoiceNumber}`));
        break;
        case 'Rejected':
       setSearchOptions(
         rejectedInvoices.map(inv => `${inv.uniqueId}|${inv.invoiceNumber}`)
       );
       break;
      default:
        setSearchOptions([]);
    }
  };

  // const matchChargesOneToOne = (shipmentNumber, carrierCharges) => {
  //   const baseline = origChargeMap[shipmentNumber] || {};
      
  //   const carrierByCode = {};
  //   carrierCharges.forEach(c => {
  //     const code = c.chargeCode;
  //     if (!carrierByCode[code]) carrierByCode[code] = [];
  //     carrierByCode[code].push({ ...c, amount: Number(c.chargeAmount) || 0 });
  //   });
    
  //   const matches = [];
    
  //   Object.entries(carrierByCode).forEach(([code, carrierList]) => {
  //     const estimatedList = Array.isArray(baseline[code]) ? [...baseline[code]] : [];
      
  //       //       //           
  //     // Create a working copy of estimated amounts with indices
  //     const estimatedWithIndex = estimatedList.map((amount, idx) => ({ amount, idx, used: false }));
      
  //     // First pass: Find exact matches
  //     carrierList.forEach(carrier => {
  //       let bestMatch = null;
  //       let bestMatchIdx = -1;
        
  //       // Look for exact match first
  //       for (let i = 0; i < estimatedWithIndex.length; i++) {
  //         if (estimatedWithIndex[i].used) continue;
          
  //         const diff = Math.abs(carrier.amount - estimatedWithIndex[i].amount);
          
  //         // Exact match found (within floating point tolerance)
  //         if (diff < 0.0001) {
  //           bestMatch = estimatedWithIndex[i];
  //           bestMatchIdx = i;
  //             //           break;
  //         }
  //       }
        
  //       // If no exact match, find closest match
  //       if (!bestMatch) {
  //         let minDiff = Infinity;
  //         for (let i = 0; i < estimatedWithIndex.length; i++) {
  //           if (estimatedWithIndex[i].used) continue;
            
  //           const diff = Math.abs(carrier.amount - estimatedWithIndex[i].amount);
  //           if (diff < minDiff) {
  //             minDiff = diff;
  //             bestMatch = estimatedWithIndex[i];
  //             bestMatchIdx = i;
  //           }
  //         }
          
  //         if (bestMatch) {
  //             //         }
  //       }
        
  //       // Mark the matched estimated charge as used
  //       if (bestMatch) {
  //         estimatedWithIndex[bestMatchIdx].used = true;
          
  //         const estimatedAmount = bestMatch.amount;
  //         const difference = Math.abs(carrier.amount - estimatedAmount);
          
  //         matches.push({
  //           chargeId: carrier.id,
  //           chargeCode: code,
  //           carrierAmount: carrier.amount,
  //           estimatedAmount: estimatedAmount,
  //           difference: difference,
  //           isDiscrepant: difference > 0.0001
  //         });
  //       } else {
  //         // No estimated charge available - treat as new charge
  //           //         matches.push({
  //           chargeId: carrier.id,
  //           chargeCode: code,
  //           carrierAmount: carrier.amount,
  //           estimatedAmount: 0,
  //           difference: carrier.amount,
  //           isDiscrepant: carrier.amount > 0.0001
  //         });
  //       }
  //     });
  //   });
    
  //     //   return matches;
  // };

const isDiscrepant = (sn, c) => {
  const entered  = Number(c.chargeAmount) || 0;
  const baseline = origChargeMap[sn]?.[c.chargeCode] || 0;
  // if they didn’t add that code at all, they never render it—so no comparison needed
  return Math.abs(entered - baseline) > 0.0001;
};




  const shipmentNumber = selectedShipment || shipments[0].shipmentNumber;

  const shipment = shipments.find(s => s.shipmentNumber === shipmentNumber) 
               || shipments[0];
  
 


  async function syncChargesToShipments(invoiceNumber, payloadShipments, headers) {
    const API = process.env.REACT_APP_API_BASE_URL + "/api";
    const officeCode = headers["Office-Code"];
  
    try {
      // Get invoice charges
      const { data: chargePage } = await httpClient.post(
        `${API}/invoice-charges/search?page=0&size=${chargePageSize}`,
        { query: invoiceNumber },
         {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "Office-Code": formData.payToOfficeCode,
          }
        } 
      );
  
      const invoiceCharges = chargePage.content
        .filter(c => c.invoiceNumber === invoiceNumber)
        .filter(c => c.discrepancyStatus !== "Rejected");


        console.log("📋 Invoice charges from search:", invoiceCharges.map(ic => ({
          chargeCode: ic.chargeCode,
          discrepancyStatus: ic.discrepancyStatus,
        })));
  
      // Get shipment numbers and load shipments
      const shipmentNumbers = Array.from(
        new Set(invoiceCharges.map(ic => ic.shipmentNumber))
      );
  
      const shipments = await Promise.all(
        shipmentNumbers.map(sn =>
          httpClient
            .get(`${API}/shipments/detailed-shipment/${sn}`,  {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                "Office-Code": formData.billToOfficeCode,
              }
             })
            .then(r => r.data)
        )
      );
  
      const shipmentsByNumber = new Map(
        shipments.map(sh => [sh.shipmentNumber, sh])
      );
  
      const shipmentCharges = shipments.flatMap(sh =>
        (sh.charges || []).map(sc => ({
          shipmentNumber: sh.shipmentNumber,
          shipmentId: sh.id,
          id: sc.id,
          amount: sc.amount,
          code: sc.code,
          type: sc.type,
          currency: sc.currency,
          version: sc.version
        }))
      );
  
      const normalizeType = (raw) => {
        const TYPE_MAP = {
          "Base Charge": "COST",
          "Buy Rate": "BUY_RATE",
          "Sell Rate": "SELL_RATE",
          "Revenue Charge": "REVENUE",
        };
        return TYPE_MAP[raw] || "COST";
      };
  
      const key = c => `${c.shipmentNumber}|${c.code}|${normalizeType(c.type)}`;
      
      const invMap = new Map(
        invoiceCharges.map(ic => [
          key({ shipmentNumber: ic.shipmentNumber, code: ic.chargeCode, type: ic.chargeType }),
          ic
        ])
      );
      
      const shpMap = new Map(
        shipmentCharges.map(sc => [key(sc), sc])
      );
  
      const toCreate = [];
      const toUpdate = [];
      const toDelete = [];
  
      invoiceCharges.forEach(ic => {
        const k = key({ shipmentNumber: ic.shipmentNumber, code: ic.chargeCode, type: ic.chargeType });
        const sc = shpMap.get(k);
        if (!sc) {
          toCreate.push(ic);
        } else {
          toUpdate.push({
            id: sc.id,
            version: sc.version,
            amount: ic.chargeAmount,
            code: ic.chargeCode,
            type: normalizeType(ic.chargeType),
            currency: ic.chargeCurrency,
          });
        }
      });
  
      for (let [k, sc] of shpMap.entries()) {
        if (!invMap.has(k)) {
          toDelete.push(sc.id);
        }
      }
  
      // Execute all operations
      await Promise.all([
        // CREATE
        ...toCreate.map(ic => {
          const shipment = shipmentsByNumber.get(ic.shipmentNumber);
          return httpClient.post(
            `${API}/charges`,
            {
              amount: ic.chargeAmount,
              code: ic.chargeCode,
              type: normalizeType(ic.chargeType) || "COST",
              currency: ic.chargeCurrency || "USD",
              invoiceNumber: ic.invoiceNumber,
              shipment: { 
                id: shipment.id,
                officeCode: shipment.officeCode 
              },
              chargeApprovalStatus: "APPROVED",
            },
            { headers }
          );
        }),
        // UPDATE
        ...toUpdate.map(u =>
          httpClient.put(
            `${API}/charges/${u.id}`,
            {
              id: u.id,
              version: u.version,
              amount: u.amount,
              code: u.code,
              type: u.type,
              currency: u.currency,
              invoiceNumber,
              chargeApprovalStatus: "APPROVED",
            },
            { headers }
          )
        ),
        // DELETE
        ...toDelete.map(id =>
          httpClient.delete(`${API}/charges/${id}`, { headers })
        )
      ]);

    } catch (err) {
      console.error("❌ Charge synchronization failed:", err);
      throw err; 
    }
  }
  
  const handleSubmit = async ({ draft = false } = {}) => {
    if (!shipments || shipments.length === 0) {
      notify.error({
        title: "No shipments",
        message: "Cannot create invoice without any shipments."
      });
      return;
    }

    // LGX-1107: block a shipment number from appearing in more than one block (each copy carries its own BASE/FSC).
    const duplicateShipmentNumbersInSubmit = getDuplicateShipmentNumbers(shipments);
    if (duplicateShipmentNumbersInSubmit.length > 0) {
      notify.error({
        title: "Duplicate shipment",
        message: `Shipment ${duplicateShipmentNumbersInSubmit.join(', ')} was added more than once. Remove the duplicate block(s) before saving.`
      });
      return;
    }
  

    const hasShipment = shipments.some(s => s.shipmentNumber && s.shipmentNumber.trim() !== '');
  
    if (!hasShipment) {
      notify.error({
        title: "No shipment selected",
        message: "You must attach at least one valid shipment to create an invoice. Please search and select a shipment first."
      });
      return;
    }
  
    const hasEmptyShipment = shipments.some(s => !s.shipmentNumber || s.shipmentNumber.trim() === '');
    
    if (hasEmptyShipment) {
      notify.error({
        title: "Invalid shipments",
        message: "All shipment blocks must have a valid shipment number. Please remove empty shipment blocks or add shipment numbers."
      });
      return;
    }
  

    const hasEmptyCode = shipments.some(s => 
      s.charges.some(c => !c.isAutoPopulated && (!c.chargeCode || c.chargeCode.trim() === ''))
    );
    
    if (hasEmptyCode) {
      notify.error({
        title: "Missing charge codes",
        message: "Please select a charge code for all charges."
      });
      return;
    }

    // Parent invoices have charges aggregated server-side; skip client charge validation.
    const isParentInvoice = formData.invoiceType === "PARENT";

    if (!isParentInvoice) {
      const shipmentsWithoutCharges = shipments
        .map((s, idx) => {
          const userCharges = s.charges.filter(c => !c.isAutoPopulated);
          return userCharges.length === 0 ? idx + 1 : null;
        })
        .filter(idx => idx !== null);

      if (shipmentsWithoutCharges.length > 0) {
        notify.error({
          title: "Missing charges",
          message: `Shipment ${shipmentsWithoutCharges.join(', ')} must have at least one charge.`
        });
        return;
      }
    }

  const chargesWithoutCodes = [];
  shipments.forEach((s, sIdx) => {
    s.charges.forEach((c, cIdx) => {
      if (!c.isAutoPopulated && (!c.chargeCode || c.chargeCode.trim() === '')) {
        chargesWithoutCodes.push(`Shipment ${sIdx + 1}, Charge ${cIdx + 1}`);
      }
    });
  });
  
  if (chargesWithoutCodes.length > 0) {
    notify.error({
      title: "Missing charge codes",
      message: `Please select charge codes for: ${chargesWithoutCodes.join('; ')}`
    });
    return;
  }

  const chargesWithInvalidAmounts = [];
  shipments.forEach((s, sIdx) => {
    s.charges.forEach((c, cIdx) => {
      const amount = parseFloat(c.chargeAmount);
      const isMSG = c.chargeCode === "MSG";
  
      if (!c.isAutoPopulated && isMSG && (isNaN(amount) || amount === 0)) {
        chargesWithInvalidAmounts.push(`Shipment ${sIdx + 1}, Charge ${cIdx + 1} (MSG cannot be 0)`);
      }
  
      if (!c.isAutoPopulated && !isMSG && (isNaN(amount) || amount <= 0)) {
        chargesWithInvalidAmounts.push(`Shipment ${sIdx + 1}, Charge ${cIdx + 1}`);
      }
    });
  });
  
  if (chargesWithInvalidAmounts.length > 0) {
    notify.error({
      title: "Invalid charge amounts",
      message: `Charges cannot be 0. Check: ${chargesWithInvalidAmounts.join('; ')}`
    });
    return;
  }
  
  

  const shipmentsWithDuplicates = [];
  shipments.forEach((s, sIdx) => {
    const userCharges = s.charges.filter(c => !c.isAutoPopulated);
    const fscCount = userCharges.filter(c => c.chargeCode === 'FSC').length;
    const basCount = userCharges.filter(c => c.chargeCode === 'BAS' || c.chargeCode === 'BASE').length;
    
    if (fscCount > 1 || basCount > 1) {
      shipmentsWithDuplicates.push(sIdx + 1);
    }
  });

  if (shipmentsWithDuplicates.length > 0) {
    notify.error({
      title: "Duplicate charges",
      message: `Only one FSC/BASE charge allowed per shipment. Check shipment ${shipmentsWithDuplicates.join(', ')}`
    });
    return;
  }


  const finalInvoiceNumber = (invoiceData["Invoice Number"].value || '').trim();
  if (!finalInvoiceNumber) {
    notify.error({
      title: "Invoice number required",
      message: "Please enter an invoice number before saving."
    });
    return;
  }

  if (!formData.billToOfficeCode || !formData.payToOfficeCode) {
    notify.error({
      title: "Missing office codes",
      message: "Both Bill To and Pay To office codes are required."
    });
    return;
  }


  if (!draft) { // Skip for drafts
    const token = Cookies.get("authToken");
    const allOfficeCodes = formData.billToOfficeCode.split(',').map(code => code.trim());
    const shipmentVerificationErrors = [];
    
    for (const shipment of shipments) {
      if (!shipment.shipmentNumber) continue;
      
      let found = false;
      for (const code of allOfficeCodes) {
        try {
          await httpClient.get(
            `${API_BASE_URL}/shipments/detailed-shipment/${shipment.shipmentNumber}`,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                "Office-Code": code,
              }
            }
          );
          found = true;
          break;
        } catch (err) {
          continue;
        }
      }
      
      if (!found) {
        shipmentVerificationErrors.push(shipment.shipmentNumber);
      }
    }

    if (shipmentVerificationErrors.length > 0) {
      notify.error({
        title: "Invalid shipments",
        message: `The following shipments do not exist: ${shipmentVerificationErrors.join(', ')}. Please verify shipment numbers.`
      });
      return;
    }
  }

    const token      = Cookies.get("authToken");
    const officeCode = Cookies.get("officeCode");
    const headers    = {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${token}`,
      "Office-Code":   officeCode,
    };
  
    const shipper = formData.billToOfficeCode;
    const carrier = formData.payToOfficeCode;
    let autoApprove = false;
        if (shipper) {
      try {
        const { autoApprove: apiAuto } = await invoiceApi.getAutoApprovalConfig(shipper);
        autoApprove = apiAuto;
        
      } catch (e) { console.warn("Auto-approval lookup failed", e); }
    }



    // const updatedShipments = shipments.map(s => {
    //   const carrierCharges = s.charges.filter(c => !c.isAutoPopulated);
      
    //   // Perform one-to-one matching
    //   const matches = matchChargesOneToOne(s.shipmentNumber, carrierCharges);
    //         
    //   // Create a map of chargeId to match result
    //   const matchMap = new Map(matches.map(m => [m.chargeId, m]));
    //   return {
    //     ...s,
    //     charges: s.charges
    //       .filter(c => !c.isAutoPopulated)
    //       .map(c => {
    //         const match = matchMap.get(c.id);
    //         if (!match) {
    //           console.warn(`No match found for charge ${c.id}`);
    //           return {
    //             ...c,
    //             chargeAuditStatus: 'Failed',
    //             discrepancyStatus: 'Discrepancy',
    //             discrepancyAmount: (Number(c.chargeAmount) || 0).toFixed(2),
    //           };
    //         }

    //         return {
    //           ...c,
    //           chargeAuditStatus: match.isDiscrepant ? 'Failed' : 'Approved',
    //           discrepancyStatus: match.isDiscrepant ? 'Discrepancy' : 'No Discrepancy',
    //           discrepancyAmount: match.difference.toFixed(2),
    //         };
    //       })
    //   };
    // });
            

    // setShipments(updatedShipments);
  
 
    // const payloadShipments = updatedShipments.map(s => {
    //   // Calculate accurate total including ALL charges
    //   const allChargesTotal = calculateShipmentTotal(s.charges);
      
    //   return {
    //     shipmentNumber: s.shipmentNumber,
    //     shipmentOrigin: s.shipmentOrigin,
    //     shipmentDestination: s.shipmentDestination,
    //     equipmentNumber: s.equipmentNumber,
    //     shipmentCharge: allChargesTotal.toFixed(2), // Use calculated total
    //     shipToInfo: s.shipToInfo || [{
    //       shipToCompanyName: '',
    //       shipToAddress: '',
    //       shipToCity: '',
    //       shipToState: '',
    //       shipToZip: '',
    //       shipToCountry: '',
    //       shipToPhone: '',
    //       shipToEmail: '',
    //       shipToFax: ''
    //     }],
    //     charges: s.charges.map(c => ({ // Include ALL charges, not just non-auto
    //       chargeCode: c.chargeCode,
    //       chargeName: c.chargeName || '', 
    //       chargeAmount: parseFloat(c.chargeAmount).toFixed(2) || '0.00',
    //       chargeCurrency: c.chargeCurrency,
    //       chargeType: c.chargeType,
    //       chargeAuditStatus: c.chargeAuditStatus,
    //       discrepancyStatus: c.discrepancyStatus,
    //       discrepancyAmount: parseFloat(c.discrepancyAmount).toFixed(2) || '0.00',
    //       isAutoPopulated: c.isAutoPopulated || false
    //     }))
    //   };
    // });
    // const hasValidShipment = updatedShipments.some(s => (s.shipmentNumber || "").trim() !== "");

    // const hasDiscrepancy = updatedShipments.some(s =>
    //   (s.charges || [])
    //     .filter(c => !c.isAutoPopulated)
    //     .some(c =>
    //       String(c.discrepancyStatus || "").toLowerCase() === "discrepancy" ||
    //       String(c.chargeAuditStatus || "").toLowerCase() === "failed"
    //     )
    // );


const payloadShipments = shipments.map(s => {
  const carrierCharges = s.charges.filter(c => !c.isAutoPopulated);
  const allChargesTotal = calculateShipmentTotal(carrierCharges);

  return {
    shipmentNumber: s.shipmentNumber,
    shipmentOrigin: s.shipmentOrigin,
    shipmentDestination: s.shipmentDestination,
    equipmentNumber: s.equipmentNumber,
    shipmentCharge: allChargesTotal.toFixed(2),
    shipToInfo: s.shipToInfo || [],
    charges: carrierCharges.map(c => ({
      chargeCode: c.chargeCode,
      chargeName: c.chargeName ||
    codeMappings.find(cm => cm.code === c.chargeCode)?.description ||
    '',
      chargeAmount: parseFloat(c.chargeAmount).toFixed(2),
      chargeCurrency: c.chargeCurrency,
      chargeType: c.chargeType,
      chargeAuditStatus: null,
      discrepancyStatus: null,
      discrepancyAmount: null,
      isAutoPopulated: false
    }))
  };
});

const hasValidShipment = shipments.some(s => (s.shipmentNumber || "").trim() !== "");
    
  if (!hasValidShipment) {
    notify.warning({
      title: "No shipment selected",
      message: "Please select at least one shipment before sending."
    });
    return;
  }
  
    const shouldAutoApprove = (autoApprove === true)  && hasValidShipment;
    let finalStatus;
    
    if (draft) {
      finalStatus = "Draft";
    } else if (shouldAutoApprove) {
      finalStatus = "Approved";
    } else if (
      invoiceData["Invoice Status"].value === "Rejected" ||
      invoiceData["Invoice Status"].value === "Draft"
    ) {
      finalStatus = "Pending";
    } else {
      finalStatus = invoiceData["Invoice Status"].value;
    }

      
    
    if (!finalInvoiceNumber || finalInvoiceNumber.trim() === '') {
      notify.error({
        title: "Invoice number required",
        message: "Please enter an invoice number before saving."
      });
      return;
    }
    
  

    const invoiceTotal = payloadShipments.reduce((sum, s) => 
  sum + parseFloat(s.shipmentCharge), 0
);
  
    const payload = {
      invoiceNumber:     finalInvoiceNumber,
      invoiceDate:       draft ? invoiceData["Invoice Date"].value : new Date().toISOString().split('T')[0],
      paymentTerms:      invoiceData["Payment Terms"].value,
      currency:          invoiceData["Currency"].value,
      total:            invoiceTotal.toFixed(2),
      invoiceStatus:     finalStatus,

      ...(editingInvoiceId && draftInvoice ? {
        approvedBy: draftInvoice.approvedBy || null,
        approvalDate: draftInvoice.approvalDate || null,
        rejectedBy: draftInvoice.rejectedBy || null,
        rejectedDate: draftInvoice.rejectedDate || null,
      } : {}),
  
      carrierOfficeCode:        officeCode,
      shipmentOfficeCode:      formData.shipmentOfficeCode,
  
      billToCompanyName: formData.billToCompanyName,
      billToAddress:     formData.billToAddress,
      billToCity:        formData.billToCity,
      billToState:       formData.billToState,
      billToZip:         formData.billToZip,
      billToCountry:     formData.billToCountry,
      billToPhone:       formData.billToPhone,
      billToEmail:       formData.billToEmail,
      billToFax:         formData.billToFax,
      billToOfficeCode:  formData.billToOfficeCode,
  
      payToCompanyName:  formData.payToCompanyName,
      payToAddress:      formData.payToAddress,
      payToCity:         formData.payToCity,
      payToState:        formData.payToState,
      payToZip:          formData.payToZip,
      payToCountry:      formData.payToCountry,
      payToPhone:        formData.payToPhone,
      payToEmail:        formData.payToEmail,
      payToFax:          formData.payToFax,
      payToOfficeCode:   formData.payToOfficeCode,
  
      shipments:         payloadShipments
    };

    
      
    try {
      if (formData.invoiceType === "PARENT" && !editingInvoiceId) {
        await httpClient.post(
          `${API_BASE_URL}/invoices/create-parent-invoice`,
          null,
          {
            params: { parentShipmentNumber: formData.parentShipmentNumber, invoiceNumber: finalInvoiceNumber },
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
              "Office-Code": officeCode,
            },
          }
        );
        notify.success({
          title: "Parent Invoice Created",
          message: "Consolidated parent invoice created successfully.",
        });
        window.dispatchEvent(new CustomEvent("parentInvoiceCreated", {
          detail: { parentShipmentNumber: formData.parentShipmentNumber, invoiceNumber: finalInvoiceNumber },
        }));
      } else if (editingInvoiceId) {
                await httpClient.put(`${API_BASE_URL}/invoices/update-invoice/${editingInvoiceId}`, payload, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "Office-Code": formData.billToOfficeCode,
          }
        }
      );
        notify.success({
          title: "Invoice updated",
          message: `Invoice ${finalInvoiceNumber} updated successfully.`
        });
      } else {
                       await httpClient.post(
        `${API_BASE_URL}/invoices/create-invoice`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
              "Office-Code": formData.billToOfficeCode,
            }
          }
        );
        notify.success({
          title: "Invoice created",
          message: `New invoice ${finalInvoiceNumber} created successfully.`
        });
      }

      // if (shouldAutoApprove) {
      //         //   await syncChargesToShipments(finalInvoiceNumber, payloadShipments, {
      //     headers: {
      //       "Content-Type": "application/json",
      //       "Authorization": `Bearer ${token}`,
      //       "Office-Code": formData.billToOfficeCode,
      //     }
      //   });
      // }



// Mark shipments invoiced only when not Draft
// if (finalStatus !== "Draft") {
//   await Promise.all(
//     shipments
//       .filter(s => !!s.shipmentNumber)
//       .map(s => {
//         return axios.put(
//           `${API_BASE_URL}/shipments/${s.shipmentNumber}`,
//           { 
//             isInvoiced: true,
//             invoiceNumber: finalInvoiceNumber,  
//             invoiceStatus: finalStatus, 
//             invoiceDate: invoiceData["Invoice Date"].value          
//           },
//           {
//             headers: {
//               ...headers,
//               "Office-Code": formData.billToOfficeCode
//             }
//           }
//         );
//       })
//   );
// }
  
      dispatch(incrementAuditRefresh());
    
      
      onSave?.();
    } catch (err) {
      console.error("Error saving invoice:", err);
      
      const errorMessage = 
        err?.response?.data?.content ||       
        err?.response?.data?.message || 
        err?.message || 
        "There was an error saving the invoice.";

        if (errorMessage.includes('already invoiced') || errorMessage.includes('isInvoiced')) {
          notify.error({
            title: "Shipment already invoiced",
            message: errorMessage
          });
        }

      // LGX-960: office-specific allowed-character rule on the invoice number
      else if (errorMessage.toLowerCase().includes('invoice number is invalid')) {
        notify.error({
          title: "Invalid invoice number",
          message: errorMessage
        });
      }

      // LGX-961: invoice numbers are unique per CARRIER now, so "already in use" is carrier-relative —
      // the same number may be perfectly available to a different carrier. Surface the backend message,
      // which names the carrier, instead of overriding it with generic copy. The "already exists" test
      // is needed because the backend raises a CustomException (not a 409) and its text says
      // "already exists for carrier X", matching neither of the previous conditions.
      else if (
        err.response?.status === 409 ||
        errorMessage.toLowerCase().includes('duplicate') ||
        errorMessage.includes('already exists')
      ) {
        notify.error({
          title: "Invoice number already exists for this carrier",
          message: errorMessage
            || `Invoice number ${finalInvoiceNumber} is already in use by this carrier. Please enter a different invoice number.`
        });
      }
      else if (errorMessage.includes('FSC') || errorMessage.includes('BAS') || errorMessage.includes('BASE')) {
        notify.error({
          title: "Duplicate charges not allowed",
          message: errorMessage 
        });
      }
      else {
        notify.error({
          title: "Save failed",
          message: errorMessage
        });
      }
    }
  };

  // LGX-1107: disable Send/Save while the same shipment is added in more than one block.
  const duplicateShipmentNumbers = getDuplicateShipmentNumbers(shipments);
  const hasDuplicateShipmentBlocks = duplicateShipmentNumbers.length > 0;

                return (
    <div className={`w-full h-full flex flex-col ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
      <div className="sticky top-0 bg-white shadow-md z-50">
        <StickyHeader
          infoData={invoiceData}
          onInfoChange={(f, v) => {
            // Prevent changes to disabled fields
            if (invoiceData[f]?.disabled) {
              return; // Do nothing if field is disabled
            }
            // LGX-960: invoice numbers are always uppercase; trimmed at submit time
            const value = f === 'Invoice Number' && typeof v === 'string' ? v.toUpperCase() : v;
            setInvoiceData(prev => ({
              ...prev,
              [f]: { ...prev[f], value }
            }));
          }}
         
buttons={[
  (draftInvoice || isRejectedInvoice) && {
    label: isRejectedInvoice ? 'Delete Invoice' : 'Delete Draft',
    type: 'danger',
    onClick: () => setShowDeleteConfirm(true),
  },
  {
    label: 'Send Invoice',
    type: 'primary',
    disabled: hasDuplicateShipmentBlocks,
    onClick: async () => handleSubmit({ draft: false }),
  },
  {
    label: 'Save as Draft',
    type: 'secondary',
    disabled: hasDuplicateShipmentBlocks,
    onClick: async () => handleSubmit({ draft: true }),
  }
].filter(Boolean)}

          saveTemplateConfig={{ checked:false, onChange:()=>{} }}
          mode="templateSearch"
          modes={MODE_OPTIONS}
          selectedMode={dropdownMode}
          onModeChange={handleModeChange}
          data={searchOptions}
          selectedValue={selectedSearchValue}
          onSelectTemplate={handleSelectSearchItem} 
        />
      </div>

      <div className="flex-1 overflow-y-auto bg-white w-full p-fluid-1">
      <div className="bg-white rounded-[clamp(0.75rem,1vw,1.5rem)] shadow-fluid-card p-fluid-2">
  {/* Invoice header like ViewInvoiceTab */}
  <h3 className="text-fluid-lg font-semibold mb-fluid-2">
    Invoice Information
  </h3>

  {draftInvoice?.note && (
    <div className="mb-fluid-2 p-fluid-2 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Note</p>
      <p className="text-sm text-gray-800 dark:text-acs-dark-text whitespace-pre-wrap">{draftInvoice.note}</p>
    </div>
  )}

  {/* Bill To & Pay To in two columns */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-fluid-2">
    {/* === Bill To === */}
    <ExpandableHeader
      headerSection="Bill To"
      icon={CreditCard}
      headerType="primary"
      disableToggle={true}
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 p-fluid-3 text-fluid-base text-gray-800 bg-white">
        {/* Column 1 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">Name/Company</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToCompanyName}
            onChange={(val) => handleFormChange("billToCompanyName", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Address</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToAddress}
            onChange={(val) => handleFormChange("billToAddress", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">City</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToCity}
            onChange={(val) => handleFormChange("billToCity", val)}
          />
        </div>

        {/* Column 2 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">State</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToState}
            onChange={(val) => handleFormChange("billToState", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Country</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToCountry}
            onChange={(val) => handleFormChange("billToCountry", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Zip/Pin</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToZip}
            onChange={(val) => handleFormChange("billToZip", val)}
          />
        </div>

        {/* Column 3 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">Phone</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToPhone}
            onChange={(val) => handleFormChange("billToPhone", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Email</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToEmail}
            onChange={(val) => handleFormChange("billToEmail", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Fax</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.billToFax}
            onChange={(val) => handleFormChange("billToFax", val)}
          />
        </div>
      </div>
    </ExpandableHeader>

    {/* === Pay To === */}
    <ExpandableHeader
      headerSection="Pay To"
      icon={Wallet}
      headerType="primary"
      disableToggle={true}
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 p-fluid-3 text-fluid-base text-gray-800 bg-white">
        {/* Column 1 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">Name/Company</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToCompanyName}
            onChange={(val) => handlePayToChange("payToCompanyName", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Address</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToAddress}
            onChange={(val) => handlePayToChange("payToAddress", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">City</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToCity}
            onChange={(val) => handlePayToChange("payToCity", val)}
          />
        </div>

        {/* Column 2 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">State</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToState}
            onChange={(val) => handlePayToChange("payToState", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Country</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToCountry}
            onChange={(val) => handlePayToChange("payToCountry", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Zip/Pin</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToZip}
            onChange={(val) => handlePayToChange("payToZip", val)}
          />
        </div>

        {/* Column 3 */}
        <div className="space-y-2">
          <label className="block text-dense-xs text-gray-500">Phone</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToPhone}
            onChange={(val) => handlePayToChange("payToPhone", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Email</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToEmail}
            onChange={(val) => handlePayToChange("payToEmail", val)}
          />

          <label className="block text-dense-xs text-gray-500 mt-2">Fax</label>
          <FieldInput
            type="text"
            className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
            value={formData.payToFax}
            onChange={(val) => handlePayToChange("payToFax", val)}
          />
        </div>
      </div>
    </ExpandableHeader>
  </div>

  {/* Shipment Details section follows below; we’ll fix that in Step 3 */}

  <div className="mt-6">
  <h3 className="text-fluid-lg font-semibold mb-fluid-2">
    Shipment Details
  </h3>

  {hasDuplicateShipmentBlocks && (
    <div className="mb-fluid-2 px-3 py-2 rounded bg-red-50 border border-red-300 text-red-700 text-sm">
      Shipment {duplicateShipmentNumbers.join(', ')} was added more than once — remove the duplicate block(s) before saving.
    </div>
  )}

  {shipments.map((shipment, idx) => {
    const shipToInfo = Array.isArray(shipment.shipToInfo)
      ? shipment.shipToInfo
      : [{
          id: uuid(),
          shipToCompanyName: '',
          shipToAddress: '',
          shipToCity: '',
          shipToState: '',
          shipToZip: '',
          shipToCountry: '',
          shipToPhone: '',
          shipToEmail: '',
          shipToFax: ''
        }];

    return (
      <ExpandableHeader
        key={idx}
        headerSection="Shipment"
        headerLabel={`${idx + 1} – ${shipment.shipmentNumber || "N/A"}`}
        icon={Truck}
        headerType="primary"
      >
        <div className="px-fluid-3 py-fluid-3 bg-white rounded-b-md space-y-fluid-4">

          {/* ================= Shipment Basic Info ================= */}
          <div className="grid grid-cols-5 gap-4 text-sm">
            <div>
              <label className="block font-medium">Shipment Number</label>
              <FieldInput
                type="text"
                className="p-1 text-sm"
                value={shipment.shipmentNumber}
                onChange={val => handleShipmentChange(idx, 'shipmentNumber', val)}
              />
            </div>

            <div>
              <label className="block font-medium">Shipment Origin</label>
              <FieldInput
                type="text"
                className="p-1 text-sm"
                value={shipment.shipmentOrigin}
                onChange={val => handleShipmentChange(idx, 'shipmentOrigin', val)}
              />
            </div>

            <div>
              <label className="block font-medium">Shipment Destination</label>
              <FieldInput
                type="text"
                className="p-1 text-sm"
                value={shipment.shipmentDestination}
                onChange={val => handleShipmentChange(idx, 'shipmentDestination', val)}
              />
            </div>

            <div>
              <label className="block font-medium">Equipment Number</label>
              <FieldInput
                type="text"
                className="p-1 text-sm"
                value={shipment.equipmentNumber}
                onChange={val => handleShipmentChange(idx, 'equipmentNumber', val)}
              />
            </div>

            <div>
              <label className="block font-medium">Shipment Total Charge</label>
              <FieldInput
                type="text"
                className="p-1 text-sm bg-gray-100"
                value={shipment.shipmentCharge}
                disabled
                onChange={() => {}}
              />
            </div>
          </div>

          {/* ================= Approval Info ================= */}
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-4 gap-4">
              {[
                ["Approved By", approvalInfo.approvedBy],
                ["Approval Date", approvalInfo.approvalDate],
                ["Rejected By", approvalInfo.rejectedBy],
                ["Rejection Date", approvalInfo.rejectionDate],
              ].map(([label, value]) => (
                <div key={label}>
                  <label className="block font-medium">{label}</label>
                  <FieldInput
                    type="text"
                    className="p-1 text-sm bg-gray-100"
                    value={value || ""}
                    disabled
                    onChange={() => {}}
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-4">
              {[
                ["Created By", approvalInfo.createdBy],
                ["Created Date", approvalInfo.createdDate],
              ].map(([label, value]) => (
                <div key={label}>
                  <label className="block font-medium">{label}</label>
                  <FieldInput
                    type="text"
                    className="p-1 text-sm bg-gray-100"
                    value={value || ""}
                    disabled
                    onChange={() => {}}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ================= Charge Details ================= */}
          <ExpandableHeader
  headerSection="Charges"
  icon={DollarSign}
  headerType="secondary"
  disableToggle={false}
>
  <div className="space-y-3">
  {shipment.charges
  .filter(c => !c.isAutoPopulated)
  .map((charge, cIdx) => {
    return (
      <div
        key={charge.id}
        className="flex flex-wrap gap-3 p-fluid-3 text-sm border-b border-gray-200 items-end"
      >
        <div className="w-[120px]">
          <p className="text-xs text-gray-500">Charge</p>
          <div className="border border-gray-300 rounded h-[2.5rem] px-2 py-1 bg-[#e6e6f9] flex items-center">
            Charge {cIdx + 1}
          </div>
        </div>

        <div className="w-[260px]">
          <p className="text-xs text-gray-500">Charge</p>
          <FieldDropdown
            className={`w-full h-[2.5rem] text-sm border rounded bg-[#e6e6f9]
            ${
              !charge.chargeCode ? "border-red-500" : "border-gray-300"
            }`}
            value={charge.chargeCode || ""}
            options={[
              ...(charge.chargeCode &&
                !codeMappings.some(cm => cm.code === charge.chargeCode)
                  ? [{ value: charge.chargeCode, label: charge.chargeCode + (getChargeDisplayName(charge.chargeCode, charge.chargeName) ? ` - ${getChargeDisplayName(charge.chargeCode, charge.chargeName)}` : "") }]
                  : []),
              ...codeMappings.map(cm => ({
                value: cm.code,
                label: cm.code + (cm.description ? ` - ${cm.description}` : ""),
              })),
            ]}
            onChange={val => handleChargeChange(idx, charge.id, "chargeCode", val)}
          />
        </div>

        <div className="w-[140px]">
          <p className="text-xs text-gray-500">Invoiced Amount</p>
          <FieldInput
            type="number"
            className="w-full h-[2.5rem] text-sm border rounded bg-[#e6e6f9] border-gray-300"
            value={charge.chargeAmount}
            onChange={val =>
              handleChargeChange(idx, charge.id, "chargeAmount", val)
            }
          />
        </div>

    

        <div className="w-[220px]">
          <p className="text-xs text-gray-500">Charge Name</p>
          <FieldDropdown
            className="w-full min-h-[2.5rem] text-sm border border-gray-300 rounded bg-[#e6e6f9]"
            value={charge.chargeName || ""}
            options={[
              ...(charge.chargeName &&
                !codeMappings.some(cm => cm.description === charge.chargeName)
                  ? [charge.chargeName]
                  : []),
              ...codeMappings.map(cm => cm.description).filter(Boolean),
            ]}
            onChange={val =>
              handleChargeChange(idx, charge.id, "chargeName", val)
            }
          />
        </div>

        <div className="w-[140px]">
          <p className="text-xs text-gray-500">Currency</p>
          <FieldDropdown
            className="w-full min-h-[2.5rem] text-sm border border-gray-300 rounded bg-[#e6e6f9]"
            value={charge.chargeCurrency || "USD"}
            options={["USD", "EUR", "GBP"]}
            onChange={val =>
              handleChargeChange(idx, charge.id, "chargeCurrency", val)
            }
          />
        </div>

        <div className="w-[140px]">
          <p className="text-xs text-gray-500">Shipment Charge</p>
          <div className="w-full h-[2.5rem] text-sm border border-gray-300 rounded bg-gray-100 px-2 py-1 flex items-center">
          {formatMoney(
              getEstimatedAmount(shipment.shipmentNumber, charge.chargeCode),
              charge.chargeCurrency
            ) ?? "—"}
          </div>
        </div>

        {isDraftOrRejectedInvoice && (
          <>
            <div className="w-[140px]">
              <p className="text-xs text-gray-500">Discrepancy Amount</p>
              <FieldInput
                type="text"
                className="w-full min-h-[2.5rem] text-sm border border-gray-300 rounded bg-[#e6e6f9]"
                value={formatMoney(charge.discrepancyAmount, charge.chargeCurrency)}
                disabled
                onChange={() => {}}
              />
            </div>

            <div className="w-[140px]">
              <p className="text-xs text-gray-500">Audit Status</p>
              <FieldInput
                type="text"
                className="w-full min-h-[2.5rem] text-sm border border-gray-300 rounded bg-[#e6e6f9]"
                value={charge.discrepancyStatus || charge.chargeAuditStatus || ""}
                disabled
                onChange={() => {}}
              />
            </div>
          </>
        )}

        <div className="w-full flex justify-end pt-2 border-t border-gray-200">
          <button
            type="button"
            className="text-red-500 underline text-sm"
            onClick={() => handleRemoveCharge(idx, charge.id)}
          >
            Remove Charge
          </button>
        </div>
      </div>
    );
  })}
<div className="flex justify-end mt-3 mb-1">
  <Button
    text="+ Add Charge"
    type="secondary"
    onClick={() => handleAddCharge(idx)}
    className="h-8 min-h-0 px-3 text-xs"
  />
</div>
  </div>
</ExpandableHeader>


          {/* ================= Ship To ================= */}
          <ExpandableHeader
            headerSection="Ship To"
            icon={MapPin}
            headerType="secondary"
            disableToggle
          >
            <div className="p-fluid-3 space-y-4">
  {shipToInfo.map((shipTo, sIdx) => (
    <div key={shipTo.id} className="bg-white p-4 rounded-md">
      <h4 className="font-semibold mb-2">
        Ship To {sIdx + 1}
      </h4>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-fluid-base text-gray-800">
        {[
          ["Name/Company", "shipToCompanyName"],
          ["Address", "shipToAddress"],
          ["City", "shipToCity"],
          ["State", "shipToState"],
          ["Zip/Pin", "shipToZip"],
          ["Country", "shipToCountry"],
          ["Phone", "shipToPhone"],
          ["Email", "shipToEmail"],
          ["Fax", "shipToFax"],
        ].map(([label, field]) => (
          <div key={field}>
            <label className="block text-dense-xs text-gray-500">
              {label}
            </label>
            <FieldInput
              type="text"
              className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] text-sm"
              value={shipTo[field] ?? ""}
              onChange={val =>
                handleShipToChange(idx, shipTo.id, field, val)
              }
            />
          </div>
        ))}
      </div>
    </div>
  ))}
</div>

          </ExpandableHeader>

          {/* ================= Remove Shipment ================= */}
          <div className="text-right">
            <button
              type="button"
              className="text-red-500 underline text-sm"
              onClick={() => handleRemoveShipmentBlock(idx)}
            >
              Remove Shipment
            </button>
          </div>

        </div>
      </ExpandableHeader>
    );
  })}
</div>


          <div className="flex justify-end items-center mt-6 gap-6">
            <Button text="   Add Shipment" onClick={handleAddShipmentBlock} className="px-6 py-3" />
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-acs-dark-text mb-2">
                {draftInvoice ? 'Delete Draft Invoice' : 'Delete Invoice'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-acs-dark-subtext mb-4">
                Are you sure you want to delete this {draftInvoice ? 'draft' : 'rejected'} invoice? This action cannot be undone.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 rounded text-sm bg-gray-400 text-white hover:bg-gray-500 transition-none"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteInvoice}
                    className="px-4 py-2 rounded text-sm bg-red-600 text-white hover:bg-red-700 transition-none flex items-center space-x-1"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default NewCarrierInvoice;
